import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TextList3.css";
import { shallowEqual, useSelector } from "react-redux";
import { useGetDateToDisplay } from "../../utils/dateFunctions";
import { selectCountryLanguageCodes } from "../../redux/authSlice";
import { Button } from "../Button";
import { APP_COLORS } from "../../constants/styles";
import useCustomTranslation from "../../utils/useCustomTranslation";
import ImageWithObjectFit from "../ImageWrapperComponent";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { parseWithRemoveBrTagsFromBody } from "../../utils/articleHelpers";

function TextList3({
    heading,
    contents: contentToRender = [],
    colors: colorObj,
    number_of_rows,
    block_source,
}) {
    const [visibleCount, setVisibleCount] = useState(3);
    const loadTriggerRef = useRef(null);

    const content = useSelector((state) => state.layout.content);
    const getDateToDisplay = useGetDateToDisplay();
    const { languageCode } = useSelector(selectCountryLanguageCodes, shallowEqual);
    const { t, direction } = useCustomTranslation();

    const fullContentList = useMemo(() => {
        const contentKey = contentToRender?.[0]?.contentKey;
        const respKeys = contentKey ? content[contentKey] : [];
        return respKeys?.map((k) => content[k]) || [];
    }, [block_source, contentToRender, content]);

    const contentReferenceData = useMemo(() => {
        return fullContentList.slice(0, visibleCount);
    }, [fullContentList, visibleCount]);

    const handleLoadMore = () => {
        const nextCount = visibleCount + 3;
        setVisibleCount(nextCount);
    };

    useEffect(() => {
        if (number_of_rows !== 1) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    handleLoadMore();
                }
            },
            { rootMargin: "100px" },
        );

        if (loadTriggerRef.current) {
            observer.observe(loadTriggerRef.current);
        }

        return () => {
            if (loadTriggerRef.current) {
                observer.unobserve(loadTriggerRef.current);
            }
        };
    }, [visibleCount, number_of_rows]);

    const showLoadMore = fullContentList.length > contentReferenceData.length;

    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.params.navigation.prevEl = prevRef.current;
            swiperRef.current.params.navigation.nextEl = nextRef.current;
            swiperRef.current.navigation.destroy();
            swiperRef.current.navigation.init();
            swiperRef.current.navigation.update();
        }
    }, []);

    const swiperCarousel = {
        spaceBetween: 10,
        slidesPerView: 1.1,
        modules: [Pagination, Navigation],
        pagination: { clickable: true, dynamicBullets: true, dynamicMainBullets: 7 },
        navigation: {
            prevEl: prevRef.current,
            nextEl: nextRef.current,
        },
        onBeforeInit: (swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
        },
        breakpoints: {
            768: {
                slidesPerView: 3,
            },
        },
        onSwiper: (swiper) => (swiperRef.current = swiper),
    };

    return (
        <section className="txtLinkWrapper3 py-50" style={APP_COLORS(colorObj)}>
            <div className="containerWrapper">
                <div className="container">
                    <div className="row">
                        <div className="col-12 mb-6">
                            <h2 className="text-accent_color1">{heading}</h2>
                            <div className="line bg-primary_color"></div>
                        </div>
                        <div className="col-12">
                            {contentReferenceData.map((card) => {
                                const slides = card.image_carousel?.length
                                    ? card.image_carousel
                                    : [card.image];

                                const isSingleSlide = slides.length <= 1;

                                return (
                                    <div
                                        className="txtLinkHldr border-b border-solid border-accent_color2"
                                        data-aos="fade-up"
                                        key={card.title}
                                    >
                                        <div className="relative swiperMain">
                                            <button
                                                ref={prevRef}
                                                className="swiper-button-prev-custom text-primary_color"
                                            />
                                            <button
                                                ref={nextRef}
                                                className="swiper-button-next-custom text-primary_color"
                                            />

                                            <PhotoProvider>
                                                <Swiper
                                                    dir={direction}
                                                    key={direction + card.title}
                                                    {...swiperCarousel}
                                                    className={isSingleSlide ? "no-pagination" : ""}
                                                >
                                                    {slides.map((img, idx) => (
                                                        <SwiperSlide key={idx}>
                                                            <div className="cardImg">
                                                                <PhotoView src={img?.src}>
                                                                    <ImageWithObjectFit
                                                                        src={img?.src}
                                                                        style={{
                                                                            cursor: "zoom-in",
                                                                        }}
                                                                    />
                                                                </PhotoView>
                                                            </div>
                                                        </SwiperSlide>
                                                    ))}
                                                </Swiper>
                                            </PhotoProvider>
                                        </div>

                                        <div className="txtLinkWrapper3Main">
                                            <h2 className="text-accent_color1 mt-5">
                                                {card.title}
                                            </h2>
                                            <div className="minutWrapper">
                                                <span className="minut text-accent_color3">
                                                    {getDateToDisplay(card.date, languageCode)}
                                                    {card.date && card.author ? " • " : ""}
                                                    {card.author && t("constantString.by")}{" "}
                                                    {card?.author}
                                                </span>
                                                <span className="minut text-accent_color4">
                                                    {parseWithRemoveBrTagsFromBody(
                                                        card.summary || "",
                                                        colorObj?.primary_color,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {showLoadMore &&
                                (number_of_rows === 1 ? (
                                    <div ref={loadTriggerRef} style={{ height: "1px" }} />
                                ) : (
                                    <div className="text-start mt-4">
                                        <Button
                                            className="button max-w-auto p-2 px-6 text-accent_color4"
                                            data-aos="fade-up"
                                            onClick={handleLoadMore}
                                            style={{ "--primary_color": colorObj?.primary_color }}
                                        >
                                            <span>{t("constantString.load_more")}</span>
                                        </Button>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default TextList3;
